import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import styles from './ConversationHistorySidebar.module.scss'
import { ConversationSession } from '../../../../../utils/conversationStorage.ts'
import { getConversationalQuickActions } from '../../../../../utils/conversationalMode/conversationalQuickActions.ts'
import Logo from '../../../../../assets/images/Logo-edify-no-bg.png'
import LogoCutted from '../../../../../assets/images/LogoEdifyNavCutted.png'

function getUserDisplayName(userData: unknown): string {
  if (!userData || typeof userData !== 'object') return 'Usuario'
  const u = userData as Record<string, unknown>
  if (u.type === 'COMPANY' && typeof u.razon_social === 'string' && u.razon_social.trim()) {
    return u.razon_social.trim()
  }
  const name = typeof u.name === 'string' ? u.name : ''
  const last = typeof u.last_name === 'string' ? u.last_name : ''
  const full = `${name} ${last}`.trim()
  return full || 'Usuario'
}

function getUserPlanLabel(userData: unknown): string {
  if (!userData || typeof userData !== 'object') return 'Plan'
  const sub = (userData as { subscription?: { plan_name?: string } }).subscription
  const p = sub?.plan_name
  if (typeof p === 'string' && p.trim()) return p.trim()
  return 'Plan'
}

function getUserProfileImageUrl(userData: unknown): string | null {
  if (!userData || typeof userData !== 'object') return null
  const u = (userData as { image_profile?: string }).image_profile
  if (typeof u === 'string' && u.trim()) return u.trim()
  return null
}

function footerMonogram(userData: unknown): string {
  const n = getUserDisplayName(userData)
  const ch = n.trim().charAt(0)
  return ch ? ch.toUpperCase() : '?'
}

interface ConversationHistorySidebarProps {
  sessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  userId?: string
  userData?: any
  onOpenSearchModal?: () => void
  onSessionShortcut?: (message: string) => void | Promise<void>
  shortcutActionsDisabled?: boolean
  onOpenOwnProfile?: () => void
}

const ConversationHistorySidebar: React.FC<ConversationHistorySidebarProps> = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  isCollapsed = false,
  onToggleCollapse,
  userId,
  userData,
  onOpenSearchModal,
  onSessionShortcut,
  shortcutActionsDisabled = false,
  onOpenOwnProfile
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fadeTop, setFadeTop] = useState(false)
  const [fadeBottom, setFadeBottom] = useState(false)
  const [profileImageFailed, setProfileImageFailed] = useState(false)
  const profileImageUrl = getUserProfileImageUrl(userData)
  const sidebarShortcuts = useMemo(() => getConversationalQuickActions(userData), [userData])

  useEffect(() => {
    setProfileImageFailed(false)
  }, [profileImageUrl])

  const handleOpenSearch = () => {
    onOpenSearchModal?.()
  }

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      setFadeTop(false)
      setFadeBottom(false)
      return
    }
    const { scrollTop, scrollHeight, clientHeight } = el
    setFadeTop(scrollTop > 4)
    setFadeBottom(scrollTop + clientHeight < scrollHeight - 4)
  }, [])

  useEffect(() => {
    updateScrollFades()
  }, [sessions.length, onSessionShortcut, sidebarShortcuts.length, updateScrollFades])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(() => updateScrollFades())
    ro.observe(el)
    return () => ro.disconnect()
  }, [updateScrollFades])

  const getPreview = (session: ConversationSession) => {
    const fromTitle = typeof session.title === 'string' && session.title.trim().length > 0
      ? session.title.trim()
      : ''
    if (fromTitle) {
      return fromTitle.length > 50 ? fromTitle.substring(0, 50) + '...' : fromTitle
    }
    const firstUserMessage = session.messages.find(m => m.role === 'user')
    if (firstUserMessage) {
      return firstUserMessage.content.length > 50
        ? firstUserMessage.content.substring(0, 50) + '...'
        : firstUserMessage.content
    }
    return 'Nueva conversación'
  }

  const showFooter = Boolean(!isCollapsed && (userId || userData))
  const planLabel = getUserPlanLabel(userData)
  const displayName = getUserDisplayName(userData)
  const showProfilePhoto = Boolean(profileImageUrl && !profileImageFailed)

  return (
      <div className={`${styles.historySidebar} ${isCollapsed ? styles.historySidebarCollapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          {isCollapsed ? (
            <div
              className={styles.logoContainerSidebarCollapsed}
              onClick={() => window.location.href = '/dashboard'}
            >
              <img src={LogoCutted} alt="Logo Edify" className={styles.logoCuttedSidebar} />
              {onToggleCollapse && (
                <button
                  className={styles.expandButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleCollapse()
                  }}
                  type="button"
                  aria-label="Expandir sidebar"
                >
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          ) : (
            <div
              className={styles.logoContainerSidebar}
              onClick={() => window.location.href = '/dashboard'}
            >
              <img src={Logo} alt="Logo Edify" className={styles.logoSidebar} />
              {onToggleCollapse && (
                <button
                  className={styles.collapseButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleCollapse()
                  }}
                  type="button"
                  aria-label="Colapsar sidebar"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        {isCollapsed ? (
          <div className={styles.collapsedActions}>
            <button
              className={styles.collapsedActionButton}
              onClick={onCreateNewSession}
              type="button"
              aria-label="Nueva conversación"
              title="Nueva conversación"
            >
              <Plus size={20} />
            </button>
            <button
              className={styles.collapsedActionButton}
              onClick={handleOpenSearch}
              type="button"
              aria-label="Buscar chats"
              title="Buscar chats"
            >
              <Search size={20} />
            </button>
          </div>
        ) : (
          <>
            <div className={styles.sidebarPinnedTop}>
              <button
                className={styles.newChatButtonItem}
                onClick={onCreateNewSession}
                type="button"
                aria-label="Nueva conversación"
              >
                <Plus size={18} />
                <span>Nueva conversación</span>
              </button>

              {onOpenSearchModal && (
                <button
                  className={styles.searchChatButtonItem}
                  onClick={handleOpenSearch}
                  type="button"
                  aria-label="Buscar chats"
                >
                  <Search size={18} />
                  <span>Buscar chats</span>
                </button>
              )}
            </div>

            <div className={styles.historyScrollShell}>
              {fadeTop ? <div className={`${styles.scrollFade} ${styles.scrollFadeTop}`} aria-hidden /> : null}
              <div
                ref={scrollRef}
                className={styles.historyScrollInner}
                onScroll={updateScrollFades}
              >
                {onSessionShortcut ? (
                  <>
                    <h3 className={styles.historyTitle}>Accesos rápidos</h3>
                    {sidebarShortcuts.map((s) => {
                      const Icon = s.icon
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={styles.newChatButtonItem}
                          disabled={shortcutActionsDisabled}
                          onClick={() => void onSessionShortcut(s.message)}
                        >
                          <Icon size={18} strokeWidth={1.85} className={styles.listRowLeadingIcon} aria-hidden />
                          <span>{s.label}</span>
                        </button>
                      )
                    })}
                  </>
                ) : null}
                <h3
                  className={
                    onSessionShortcut
                      ? `${styles.historyTitle} ${styles.historyTitleSpaced}`
                      : styles.historyTitle
                  }
                >
                  Historial
                </h3>
                {sessions.length === 0 ? (
                  <div className={styles.emptySessions}>
                    <MessageSquare size={32} className={styles.emptyIcon} />
                    <p className={styles.emptyText}>No hay conversaciones aún</p>
                  </div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.sessionId}
                      className={`${styles.sessionItem} ${
                        session.sessionId === currentSessionId ? styles.sessionItemActive : ''
                      }`}
                      onClick={() => onSelectSession(session.sessionId)}
                    >
                      <div className={styles.sessionContent}>
                        <div className={styles.sessionPreview}>
                          <span className={styles.sessionText}>{getPreview(session)}</span>
                        </div>
                      </div>
                      <button
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDeleteSession(session.sessionId)
                        }}
                        type="button"
                        aria-label="Eliminar conversación"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
              {fadeBottom ? <div className={`${styles.scrollFade} ${styles.scrollFadeBottom}`} aria-hidden /> : null}
            </div>

            {showFooter ? (
              onOpenOwnProfile ? (
                <button
                  type="button"
                  className={styles.sidebarFooter}
                  onClick={onOpenOwnProfile}
                  aria-label="Ir a mi perfil"
                >
                  <span
                    className={`${styles.footerAvatar} ${showProfilePhoto ? styles.footerAvatarHasPhoto : ''}`}
                    aria-hidden
                  >
                    {showProfilePhoto ? (
                      <img
                        src={profileImageUrl!}
                        alt=""
                        className={styles.footerAvatarImg}
                        onError={() => setProfileImageFailed(true)}
                      />
                    ) : (
                      footerMonogram(userData)
                    )}
                  </span>
                  <span className={styles.footerMeta}>
                    <span className={styles.footerName}>{displayName}</span>
                    <span className={styles.footerPlan}>{planLabel}</span>
                  </span>
                </button>
              ) : (
                <div className={styles.sidebarFooter}>
                  <span
                    className={`${styles.footerAvatar} ${showProfilePhoto ? styles.footerAvatarHasPhoto : ''}`}
                    aria-hidden
                  >
                    {showProfilePhoto ? (
                      <img
                        src={profileImageUrl!}
                        alt=""
                        className={styles.footerAvatarImg}
                        onError={() => setProfileImageFailed(true)}
                      />
                    ) : (
                      footerMonogram(userData)
                    )}
                  </span>
                  <span className={styles.footerMeta}>
                    <span className={styles.footerName}>{displayName}</span>
                    <span className={styles.footerPlan}>{planLabel}</span>
                  </span>
                </div>
              )
            ) : null}
          </>
        )}
      </div>
  )
}

export default ConversationHistorySidebar
